import "./timeline.scss"

import { ControlType, FetchStatus, IDataSource, IEntity, Property, PropertyType } from "../../../base/types";
import { DataContextProvider, useDataContext } from "../../page/context";
import { FC, useEffect, useState } from "react";

import { DesignerMode } from "../../types";
import { IControlAttributes } from "../../../base/models";
import { ListRows } from "../lists";
import React from "react";
import { Registry } from "../../../base/register";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import { useGetProperties } from "../../../base/hooks";

export const TimelineComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties, getBinding } = useGetProperties({ bindings: control.bindings });
    const oldestOnTop: boolean | undefined = properties[Property.OldestOnTop];
    const twelveHourFormat: boolean | undefined = properties[Property.TwelveHourFormat];
    const [timeField, setTimeField] = useState<string>()
    const { type, result: data } = useDataContext()

    useEffect(() => {
        const binding = getBinding(Property.TimeField)
        if (binding?.type === "Field") {
            setTimeField(binding.value)
        }

        // eslint-disable-next-line
    }, [properties])

    if (designerMode === DesignerMode.Draft) {
        const timeField2 = timeField || 'time';
        const designData: IEntity[] = type === "List" && data.count > 0 ?
            [data.records[0]] :
            [{ [timeField2]: new Date() }]

        return <div className={clsx(className)}>
            <Timeline records={designData} timeField={timeField2} twelveHourFormat={twelveHourFormat}>
                {children}
            </Timeline>
        </div>
    }

    return (
        <div className={clsx(className)}>
            {(type === "List" && timeField) && <>
                <Timeline
                    records={data.records}
                    oldestOnTop={oldestOnTop}
                    timeField={timeField}
                    twelveHourFormat={twelveHourFormat}>
                    {children}
                </Timeline>
            </>}
        </div>
    )
}

interface ITimelineData {
    key: string,
    date: string,
    time: string,
    records: IEntity[]
}

const DateFormat = {
    date: (date: string) => moment(date).format('YYYY-MM-DD'),
    time24HH_MM: (date: string) => moment(date).format('HH:mm'),
    time12HH_MM: (date: string) => moment(date).format('hh:mm A'),
    dateTime: (date: string) => moment(date).format('YYYY-MMM-DD HH:mm:ss')
};

const getTimelineData = (timeField: string, records: IEntity[], oldestOnTop: boolean = false, twelveHourFormat: boolean = false) => {
    const sorted = _.orderBy(records, p => moment(p[timeField]))
    const grouped = _.groupBy(sorted, p => p[timeField])
    const items: ITimelineData[] = []

    _.forEach(grouped, (values, key) => {
        const keyText = (key && key !== 'undefined') ? DateFormat.dateTime(key) : '';

        values.forEach(value => {
            items.push({
                key: keyText,
                date: keyText ? DateFormat.date(key) : '',
                time: keyText ? (twelveHourFormat ? DateFormat.time12HH_MM(key) : DateFormat.time24HH_MM(key)) : '',
                records: [value]
            })
        })
    })

    let sortedItems = items.sort((a, b) => moment(a.key).valueOf() - moment(b.key).valueOf())
    sortedItems = oldestOnTop ? sortedItems : sortedItems.reverse();

    if (twelveHourFormat) {
        let prevDate = '';

        for (let i = 0; i < sortedItems.length; i++) {
            if (prevDate === sortedItems[i].date)
                sortedItems[i].date = '';

            prevDate = sortedItems[i].date;
        }
    }

    return sortedItems;
}

const Timeline = ({ records, timeField, oldestOnTop, twelveHourFormat, className, children }: {
    timeField: string,
    records: IEntity[],
    oldestOnTop?: boolean,
    twelveHourFormat?: boolean,
    className?: string,
    children?: React.ReactNode
}) => {
    const [items, setTimelineItems] = useState<ITimelineData[]>([]);
    const { dataSource } = useDataContext()

    useEffect(() => {
        const data = getTimelineData(timeField, records, oldestOnTop, twelveHourFormat)
        setTimelineItems(data)
        // eslint-disable-next-line
    }, [oldestOnTop, twelveHourFormat])

    return <>
        <div className={`${className} `}>
            <div className='timeline-label'>
                {items.map((item) => <React.Fragment key={item.key}>
                    <div className='timeline-item'>
                        <div className='timeline-label fw-bold text-gray-800 fs-7'>
                            <div className="text-end pe-3">
                                <p className="my-0 fw-bolder">{item.date}</p>
                                <p className="my-0">{item.time}</p>
                            </div>
                        </div>
                        <div className='timeline-badge'>
                            <i className='fa fa-genderless text-primary fs-1'></i>
                        </div>
                        <div className='timeline-content ps-3'>
                            <DataContextProvider>
                                <SetDataContext
                                    dataSource={dataSource}
                                    records={item.records} />
                                <ListRows>
                                    {children}
                                </ListRows>
                            </DataContextProvider>
                        </div>
                    </div>
                </React.Fragment>)}
            </div>
        </div>
    </>
}

const SetDataContext = ({ records, dataSource }: { dataSource?: IDataSource, records: IEntity[] }) => {
    const { setData, setDataSource } = useDataContext()

    useEffect(() => {
        if (dataSource)
            setDataSource(dataSource)
        // eslint-disable-next-line
    }, [dataSource])

    useEffect(() => {
        setData({ type: "List", result: { count: records.length, records }, status: FetchStatus.Success })
        // eslint-disable-next-line
    }, [records])

    return <></>
}

Registry.addControl({
    id: "020bc16d-06d5-4e03-8472-502c77641714",
    name: "Timeline",
    type: ControlType.Timeline,
    properties: [
        { id: Property.TimeField, name: "Time Field", type: PropertyType.String },
        { id: Property.OldestOnTop, name: "Oldest on top", type: PropertyType.Boolean },
        { id: Property.TwelveHourFormat, name: "12 Hour Format", type: PropertyType.Boolean }, // Added property
    ],
    component: TimelineComponent
})
